import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";

import { ANALYTICS_EVENTS } from "@/constants/analytics.constants";
import { posthog } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { getChatwootSsoLink } from "@/apis/agents";
import { useAgent } from "@/context/agent-provider";
import { useUser } from "@/context/user-provider";

const ChatwootIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className="h-4 w-4 shrink-0"
    aria-hidden="true"
  >
    <g clipPath="url(#chatwoot-clip)">
      <path
        d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
        fill="#404040"
      />
      <path
        d="M17.1256 17.1258H11.5622C8.4955 17.1258 5.99976 14.6299 5.99976 11.5624C5.99976 8.49591 8.4955 6 11.5623 6C14.6298 6 17.1256 8.49591 17.1256 11.5624V17.1258Z"
        fill="white"
        stroke="white"
        strokeWidth="0.28125"
      />
    </g>
    <defs>
      <clipPath id="chatwoot-clip">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const ChatwootNavItem: React.FC = () => {
  const { t } = useTranslation();
  const { login, getAccessToken } = useUser();
  const { agent, loading: agentLoading } = useAgent();

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const token = await getAccessToken();
      return getChatwootSsoLink(agent?.id ?? "", token ?? "", login);
    },
    onSuccess: (url) => {
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const isLoading = agentLoading || isPending;
  const isDisabled = !agentLoading && !agent?.id;

  return (
    <button
      type="button"
      onClick={() => {
        posthog.capture(ANALYTICS_EVENTS.CHATWOOT_LINK_OPENED);
        mutate();
      }}
      disabled={isLoading || isDisabled}
      className={cn(
        "group flex w-full items-center gap-2 rounded-lg",
        "text-text px-2 py-1.5 text-[13px] font-medium",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        "transition-opacity duration-500",
        !isLoading && !isDisabled && "hover:bg-surface-hovered cursor-pointer",
        isLoading && "animate-pulse cursor-default duration-1000",
        isDisabled && "opacity-40"
      )}
    >
      <ChatwootIcon />
      <span className="truncate">{t("Chatwoot")}</span>
      <ExternalLink className="text-subdued ml-auto size-4 shrink-0" />
    </button>
  );
};
